import { Trans, useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { slugifyId, CATALOGUE_ID_RE } from "../utils/slug";
import { friendlyError } from "../utils/apiError";
import { describeError, useToast } from "../components/toast";
import ProductPackagePicker from "../components/ProductPackagePicker";
import { foyerApi, type FeatureSetRow, type SeatUnit } from "../http/foyer";
import { PageContainer, PageHeader } from "../components/PageHeader";
import { PermissionGate } from "../auth/access";
import { AUTHOR_PACKAGES } from "../components/distribution/catalogueGates";
import { useFormat, type Formatters } from "../i18n/format";

/**
 * Products — what a customer is SOLD (`ENT-1`).
 *
 * ## Why this page exists at all
 *
 * Distribution led with **Packages**, which is a content-curation tool: 244
 * rows of "which titles are in this bag". Nothing on that screen said what
 * anybody had *bought*, so the first thing an operator met was implementation
 * detail. This is the level people actually think at — an offering with a name
 * and a price — and the package library sits underneath it.
 *
 * ## What the data says, and why the page says it out loud
 *
 * Measured 2026-08-17 against the real portal: **232 offerings, and every single
 * one grants exactly ONE package.** The layer that exists to bundle packages
 * bundles nothing, and the offerings are duplicated per business unit exactly
 * like the packages beneath them — 91 distinct names across 14 units, with
 * `entertain` alone existing 16 times.
 *
 * That is worth showing rather than hiding behind a tidy grid, because it is
 * the whole argument for `ENT-3`: an operator who sees "entertain ×16"
 * understands the cleanup in a way no summary will achieve. So duplicates are
 * GROUPED, and the count is the headline.
 *
 * ## What this page deliberately does not do
 *
 * It does not edit price, type or `externalRef`. Those are the portal's or the
 * CRM's to own during the parallel period, and a second writer for a commercial
 * fact is precisely what `parallel-operation-contract` exists to prevent. The
 * only write is which packages an offering grants.
 */

interface Group {
  /** The offering name with its `(Business Unit)` suffix removed. */
  name: string;
  rows: FeatureSetRow[];
}

/** `entertain (Standalone Hotels)` → `entertain`. */
function baseName(name: string): string {
  return name.replace(/\s*\([^)]*\)\s*$/, "").trim() || name;
}

function priceLabel(fmt: Formatters, row: FeatureSetRow): string | null {
  const amount = row.price?.amount;
  if (typeof amount !== "number") return null;
  const currency = row.price?.currency;
  return currency ? fmt.currency(amount, currency) : fmt.number(amount);
}

/** An empty cell: a symbol, not a word, so it is the same in every language. */
const NONE = "—";

function businessUnits(group: Group): string {
  return group.rows
    .map((r) => r.businessUnit || NONE)
    .sort()
    .join(" · ");
}

export default function ContentProductsPage() {
  const { t } = useTranslation(["content", "common"]);
  const [rows, setRows] = useState<FeatureSetRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  const [creating, setCreating] = useState(false);
  const [editingGrants, setEditingGrants] = useState<FeatureSetRow | null>(
    null
  );

  const load = useCallback(async () => {
    try {
      const res = await foyerApi.listFeatureSets();
      /**
       * CHANNEL products only.
       *
       * 🚨 Nenda's capabilities — Frontlayer, Care, Signage and the rest — are
       * CODE. They arrive with the software, cannot be created, renamed or
       * given packages, and there is nothing on this page for them to do. They
       * were shown here anyway, in the same list and the same card, which is
       * why Care looked like it wanted channels and why "grants no package"
       * was drawn in error red on a product for which granting nothing is
       * correct.
       *
       * They are still sold, and the licence dialog still offers them — the
       * route keeps returning them for exactly that. Selling is not authoring,
       * and this is the authoring screen.
       */
      setRows(res.featureSets.filter((f) => !f.capability));
      setError(null);
    } catch (e) {
      setError(friendlyError(t, e));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const groups: Group[] = useMemo(() => {
    const by = new Map<string, FeatureSetRow[]>();
    for (const row of rows) {
      const key = baseName(row.name);
      by.set(key, [...(by.get(key) ?? []), row]);
    }
    const q = query.trim().toLowerCase();
    return [...by.entries()]
      .map(([name, list]) => ({ name, rows: list }))
      .filter(
        (g) =>
          !q ||
          g.name.toLowerCase().includes(q) ||
          g.rows.some((r) =>
            r.packages.some((p) => p.name.toLowerCase().includes(q))
          )
      )
      .sort(
        (a, b) => b.rows.length - a.rows.length || a.name.localeCompare(b.name)
      );
  }, [rows, query]);

  const bundling = rows.filter((r) => r.packages.length > 1).length;
  const missing = rows.filter((r) => r.packages.some((p) => p.missing)).length;
  const duplicated = groups.filter((g) => g.rows.length > 1).length;

  return (
    <PageContainer>
      <PageHeader
        eyebrow={t("common:labels.distribution")}
        title={t("contentProductsPage.title")}
        subtitle={t("contentProductsPage.subtitle")}
        actions={
          <PermissionGate {...AUTHOR_PACKAGES}>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setCreating(true)}
              data-testid="product-new"
            >
              {t("contentProductsPage.newProduct")}
            </Button>
          </PermissionGate>
        }
      />

      {editingGrants ? (
        <ProductPackagePicker
          key={editingGrants.id}
          productId={editingGrants.id}
          productName={editingGrants.name}
          granted={editingGrants.packages}
          seatUnit={editingGrants.seatUnit ?? null}
          onClose={() => setEditingGrants(null)}
          onSaved={async () => {
            setEditingGrants(null);
            await load();
          }}
        />
      ) : null}

      {creating ? (
        <CreateProductDialog
          onClose={() => setCreating(false)}
          onCreated={async () => {
            setCreating(false);
            await load();
          }}
        />
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Stack>
      ) : (
        <>
          {rows.length === 0 ? (
            // Not an error — an empty catalogue is the correct state before
            // anybody has defined what Nenda sells. It says what to do next
            // rather than what is missing.
            <Alert severity="info" data-testid="products-empty">
              {t("contentProductsPage.empty")}
            </Alert>
          ) : (
            <>
              {bundling === 0 ? (
                <Alert
                  severity="warning"
                  sx={{ mb: 3 }}
                  data-testid="products-1to1"
                >
                  <Trans
                    t={t}
                    i18nKey="contentProductsPage.oneToOne"
                    values={{ count: rows.length }}
                    components={{ strong: <strong /> }}
                  />
                </Alert>
              ) : null}

              {missing > 0 ? (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {t("contentProductsPage.missing", { count: missing })}
                </Alert>
              ) : null}

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ sm: "center" }}
                sx={{ mb: 3 }}
              >
                <TextField
                  size="small"
                  label={t("contentProductsPage.search")}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  sx={{ minWidth: 280 }}
                  inputProps={{ "data-testid": "products-search" }}
                />
                <Typography variant="body2" color="text.secondary">
                  <Trans
                    t={t}
                    i18nKey="contentProductsPage.totals"
                    values={{
                      products: rows.length,
                      names: groups.length,
                      duplicated,
                    }}
                    components={{ strong: <strong /> }}
                  />
                </Typography>
              </Stack>

              <Stack spacing={2}>
                {groups.map((group) => (
                  <ProductGroup
                    key={group.name}
                    group={group}
                    onEditGrants={setEditingGrants}
                  />
                ))}
              </Stack>
            </>
          )}
        </>
      )}
    </PageContainer>
  );
}

/**
 * One product, or one product duplicated N times across business units.
 *
 * Grouped rather than listed flat because the duplication IS the finding: a
 * card headed "entertain — 16 copies" makes the case for the cleanup in a way
 * sixteen identical cards never would.
 */
function ProductGroup({
  group,
  onEditGrants,
}: {
  group: Group;
  onEditGrants: (row: FeatureSetRow) => void;
}) {
  const { t } = useTranslation(["content", "common"]);
  const fmt = useFormat();
  const copies = group.rows.length;
  const first = group.rows[0];
  const price = priceLabel(fmt, first);
  const sizes = [...new Set(group.rows.map((r) => r.packages.length))];

  return (
    <Card variant="outlined" data-testid={`product-${group.name}`}>
      <CardContent>
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <Typography variant="h6" sx={{ mr: 0.5 }}>
            {group.name}
          </Typography>

          {copies > 1 ? (
            <Tooltip title={t("contentProductsPage.copiesTooltip")}>
              <Chip
                size="small"
                color="warning"
                label={t("contentProductsPage.copies", { count: copies })}
                data-testid={`product-copies-${group.name}`}
              />
            </Tooltip>
          ) : null}

          {first.type ? (
            <Chip
              size="small"
              variant="outlined"
              label={first.type}
              // `rental` reads as "the guest pays for this", which is not what
              // it does today — the play gate ignores it entirely.
              color={first.type === "rental" ? "info" : "default"}
            />
          ) : null}

          {price ? (
            <Chip size="small" variant="outlined" label={price} />
          ) : null}

          {first.externalRef ? (
            <Tooltip title={t("contentProductsPage.externalRefTooltip")}>
              <Chip size="small" variant="outlined" label={first.externalRef} />
            </Tooltip>
          ) : null}
        </Stack>

        {first.description ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {first.description}
          </Typography>
        ) : null}

        <Typography
          variant="caption"
          color="text.secondary"
          component="div"
          sx={{ mt: 2, mb: 0.5 }}
        >
          {t("contentProductsPage.grants")}
          {sizes.length > 1
            ? t("contentProductsPage.copiesDisagree", {
                sizes: sizes.sort((a, b) => a - b).join(", "),
              })
            : ""}
          {/* Only offered on a SINGLE product. A grouped card stands for
              several rows that disagree, and "edit" would have to pick one
              silently — the copies are a cleanup, not something to edit
              through. */}
          {copies === 1 ? (
            <PermissionGate {...AUTHOR_PACKAGES}>
              <Button
                size="small"
                sx={{ ml: 1, py: 0, minWidth: 0 }}
                onClick={() => onEditGrants(first)}
                data-testid={`product-grants-${first.id}`}
              >
                {t("common:buttons.edit")}
              </Button>
            </PermissionGate>
          ) : null}
        </Typography>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {first.packages.length === 0 ? (
            /* An unfinished job, not a fault. This was an ERROR chip, which is
               the loudest colour the palette has, spent on the ordinary state
               of a product somebody created ten seconds ago. It says what is
               missing and offers the fix, in the tone of a hint. */
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 1,
                border: "1px dashed",
                borderColor: "divider",
                bgcolor: "action.hover",
                width: "fit-content",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {t("contentProductsPage.noPackages")}
              </Typography>
              {copies === 1 ? (
                <PermissionGate {...AUTHOR_PACKAGES}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => onEditGrants(first)}
                    data-testid={`product-choose-packages-${first.id}`}
                  >
                    {t("contentProductsPage.choosePackages")}
                  </Button>
                </PermissionGate>
              ) : null}
            </Stack>
          ) : (
            first.packages.map((pkg) => (
              <Chip
                key={pkg.id}
                size="small"
                label={pkg.name}
                color={pkg.missing ? "error" : "default"}
                variant={pkg.missing ? "filled" : "outlined"}
              />
            ))
          )}
        </Stack>

        {copies > 1 ? (
          <Typography
            variant="caption"
            color="text.secondary"
            component="div"
            sx={{ mt: 1.5 }}
          >
            {businessUnits(group)}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
}

/**
 * New channel product.
 *
 * There was no way to create one. The whole surface assumed products were
 * IMPORTED, so it could list them, group them and set their packages — and an
 * operator who wanted a new one had nowhere to start. Sigge hit it in the
 * flow he had just asked for: package created, then "there's no way to create
 * a product".
 *
 * Name first, id derived, id permanent — the same shape as the package dialog,
 * and for the same reason: `_id` is what `License.featureSet` and
 * `RoomType.featureSets` point at, Mongo will not change it, and there is no
 * rename.
 *
 * ## It creates the product EMPTY, on purpose
 *
 * Packages are granted afterwards, through the route that refuses unknown ids.
 * Asking for them here would mean two places answering "does this package
 * exist", and the second one is always the one that gets it wrong.
 */
function CreateProductDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => Promise<void>;
}) {
  const toast = useToast();
  const { t } = useTranslation(["content", "common"]);
  const [name, setName] = useState("");
  const [id, setId] = useState("");
  const [idTouched, setIdTouched] = useState(false);
  /**
   * What a licence for this product COUNTS.
   *
   * 🚨 Asked here because it is asked nowhere else, and the consequence is
   * invisible until somebody tries to sell the thing. A product created without
   * it defaults to no amount at all, and the licence dialog then hides the
   * Amount field — so a freshly authored "Basic Sweden" could be sold but not
   * quantified, and saved 40 rooms as 0.
   */
  const [seatUnit, setSeatUnit] = useState<SeatUnit>("room");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const effectiveId = idTouched ? id : slugifyId(name);
  const idValid = effectiveId === "" || CATALOGUE_ID_RE.test(effectiveId);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      await foyerApi.createFeatureSet({
        id: effectiveId,
        name: name.trim(),
        seatUnit,
      });
      toast.success(t("contentProductsPage.created", { name: name.trim() }));
      await onCreated();
    } catch (e) {
      setError(friendlyError(t, e));
      toast.error(describeError(t, "createProduct", e, { name: name.trim() }));
      setBusy(false);
    }
  };

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={busy ? undefined : onClose}>
      <DialogTitle>{t("contentProductsPage.newProduct")}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="info">
            <Trans
              t={t}
              i18nKey="contentProductsPage.createIntro"
              components={{ code: <code /> }}
            />
          </Alert>

          <TextField
            label={t("common:labels.name")}
            size="small"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("contentProductsPage.namePlaceholder")}
            inputProps={{ "data-testid": "product-new-name" }}
          />

          <TextField
            label={t("common:labels.id")}
            size="small"
            value={effectiveId}
            onChange={(e) => {
              setIdTouched(true);
              setId(e.target.value);
            }}
            error={!idValid}
            placeholder={t("contentProductsPage.idPlaceholder")}
            inputProps={{ "data-testid": "product-new-id" }}
            helperText={
              !idValid
                ? t("contentProductsPage.idInvalid")
                : t("contentProductsPage.idHelp")
            }
            sx={{ "& input": { fontFamily: "fontFamilyMono" } }}
          />

          <TextField
            label={t("productPackagePicker.seatUnitLabel")}
            select
            size="small"
            value={seatUnit ?? "none"}
            onChange={(e) =>
              setSeatUnit(
                e.target.value === "none" ? null : (e.target.value as SeatUnit)
              )
            }
            inputProps={{ "data-testid": "product-new-seat-unit" }}
            helperText={t("productPackagePicker.seatUnitHelp")}
          >
            {/* Each option is the sentence an operator would say out loud.
                "No amount" read as "free"; "Nothing to count" says what it is.
                Screens is the one that moves the pairing cap, so it says so. */}
            <MenuItem value="room">
              {t("productPackagePicker.seatUnit.room")}
            </MenuItem>
            <MenuItem value="screen">
              {t("productPackagePicker.seatUnit.screen")}
            </MenuItem>
            <MenuItem value="resident">
              {t("productPackagePicker.seatUnit.resident")}
            </MenuItem>
            <MenuItem value="none">
              {t("productPackagePicker.seatUnit.none")}
            </MenuItem>
          </TextField>

          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t("common:buttons.cancel")}
        </Button>
        <PermissionGate {...AUTHOR_PACKAGES} deniedProps={{ disabled: true }}>
          <Button
            variant="contained"
            onClick={() => void create()}
            disabled={busy || !effectiveId || !idValid}
            data-testid="product-new-create"
          >
            {t("common:buttons.create")}
          </Button>
        </PermissionGate>
      </DialogActions>
    </Dialog>
  );
}
